import { FormEvent, useState } from "react";
import { postAnnotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/model/services/postAnnotation";
import styles from "./NewAnnotation.module.scss";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Textarea } from "@/frontend/shared/ui/Textarea/Textarea";
import { Typography } from "@/frontend/shared/ui/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { UsersAvatar } from "@/frontend/shared/ui/UsersAvatar/UsersAvatar";
import { getOrganizationById } from "@/frontend/pages/SettingsPage/model/services/getOrganizationById";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { patchAnnotation } from "@/frontend/pages/CompanyProfilePage/ui/AnnotationsSection/model/services/patchAnnotation";
import { ArrowUpIcon } from "@/frontend/shared/assets/icons";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";

export function NewAnnotation({
  annotationId,
  companyId,
  onCancel,
  content: contentProp,
  patch,
  onSuccess,
  replyToId,
  compact,
  flat,
  organizationId,
  textAreaPlaceholder
}: {
  annotationId?: number;
  companyId: number;
  onCancel?: () => void;
  onSuccess?: () => void;
  content?: string;
  patch?: boolean;
  replyToId?: number;
  compact?: boolean;
  flat?: boolean;
  organizationId?: number;
  textAreaPlaceholder?: string;
}) {
  const queryClient = useQueryClient();
  const [isPosting, setIsPosting] = useState(false);
  const { derived, data } = useMe();
  const [content, setContent] = useState(contentProp || "");

  const { data: orgData } = useQuery({
    queryKey: ["organizationData", data?.organizationId],
    queryFn: () => (data?.organizationId ? getOrganizationById(data.organizationId) : null),
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "INVESTOR PORTFOLIO COMPANY PAGE: /experts/organizations");
      return true;
    }
  });

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setIsPosting(true);
      if (patch && annotationId) await patchAnnotation({ id: annotationId, content });
      else await postAnnotation({ companyId, content, replyToId, organizationId });
      await queryClient.invalidateQueries({ queryKey: ["annotationsData" + companyId] });
      setIsPosting(false);
      setContent("");
      onSuccess && onSuccess();
      snackbarConstructor(patch ? "Note updated successfully" : "Note created successfully", "normal");
    } catch (err) {
      snackbarConstructor(patch ? "Failed to update note" : "Failed to create note", "error");
      setIsPosting(false);
    }
  };
  const isMobile = useScreenWidth() <= 768;

  return (
    <div className={flat ? styles.flat : styles.card}>
      {!isMobile && (
        <div>
          <UsersAvatar
            className={styles.avatar}
            nameInitials={derived.nameInitials}
            profileImg={derived.profileImageUrl}
          />
        </div>
      )}

      <div className={styles.content}>
        <form className={styles.form} onSubmit={onSubmit}>
          <Textarea
            rows={compact ? 2 : 4}
            placeholder={textAreaPlaceholder ?? "Write your thoughts"}
            name="content"
            defaultValue={contentProp}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
            }}
          ></Textarea>
          {!compact && (
            <Typography variant="body-3" color="greyscale500">
              This note will be visible to all members within the {orgData?.name ?? ""} organization
            </Typography>
          )}
          {compact && (
            <div className={styles.floatingPostBtnContainer}>
              <NormalButton
                type="submit"
                size="medium"
                disabled={isPosting || !content}
                className={styles.floatingPostBtn}
              >
                <ArrowUpIcon />
              </NormalButton>
            </div>
          )}

          {!compact && (
            <div className={styles.buttonsContainer}>
              <NormalButton type="submit" size="large" disabled={isPosting || !content}>
                Post
              </NormalButton>
              {onCancel && (
                <NormalButton variant="tertiary" size="medium" onClick={onCancel}>
                  Cancel
                </NormalButton>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
